import { getPageTitleTranslate } from "@/helpers/services/routes.js";
import { modules } from "@/helpers/variables/modules";
import { initializeClarity } from "@/plugins/clarity";
import { generalRoutes } from "@/router/routes/general";
import { signRoutes } from "@/router/routes/sign";
import { suiteRoutes } from "@/router/routes/suite";
import { trackRoutes } from "@/router/routes/track";
import {
  retry,
  stopAfterAttempt,
  waitExponential,
} from "@/services/RetryService.js";
import store from "@/store";
import Vue from "vue";
import VueRouter, { START_LOCATION } from "vue-router";

Vue.use(VueRouter);

const routes = [
  ...generalRoutes,
  ...suiteRoutes,
  ...signRoutes,
  ...trackRoutes,
];

const router = new VueRouter({
  mode: "history",
  routes,
});

let clarityInitialized = false;

const maxRetriesCount = process.env.VUE_APP_MAX_LOGIN_RETRIES_COUNT
  ? parseInt(process.env.VUE_APP_MAX_LOGIN_RETRIES_COUNT)
  : 10;

const minWaitTime = process.env.VUE_APP_MAX_WAIT_TIME
  ? parseInt(process.env.VUE_APP_MAX_WAIT_TIME)
  : 8000;

const maxWaitTime = process.env.VUE_APP_MAX_WAIT_TIME
  ? parseInt(process.env.VUE_APP_MAX_WAIT_TIME)
  : 20000;

export function setRouterGlobalGuards() {
  router.beforeEach(async (to, from, next) => {
    store.commit("setLoading", true);

    const { isAuthenticated } = store.getters;

    const tryToAuthenticate =
      (to.meta.requiresAuth && !isAuthenticated) || to.meta.tryToAuthenticate;

    store.commit("closeControlCard");

    if (tryToAuthenticate) {
      commitToMetaApp(to.meta.app);
    }

    const breadCrumb = to.meta.breadCrumb;

    if (breadCrumb && breadCrumb[breadCrumb.length - 1]) {
      const lastBreadCrumbText = breadCrumb[breadCrumb.length - 1].text;
      store.commit("setFunctionalityClicked", lastBreadCrumbText);
    }

    if (to.name === "TemplateEdit") {
      store.commit("setSidebarMini", true);
    }

    if (to.query?.action === "allow_trial") {
      localStorage.setItem("allow_trial", "allowed");
    }

    next();

    window.scrollTo({ top: 0, behavior: "smooth" });
  });

  router.afterEach((to) => {
    const routeError = store.getters.routeError;
    const permissionDialogWithDelay = store.getters.permissionDialogWithDelay;
    const showOnboardingUser = store.getters.showOnboardingUser;

    store.commit("setCurrentRoute", to.name); // Adiciona nome da rota atual no store
    let title = getPageTitleTranslate(to.name);
    if (title) document.title = `${title}` + " - Conecta Suite";

    if (routeError.code) {
      commitErrorReport(routeError.code, routeError.slug);
      store.commit("updateRouteError", {});
    }

    if (permissionDialogWithDelay) {
      store.commit("setPermissionDialog", permissionDialogWithDelay);
    }

    localStorage.setItem("show_onboarding", showOnboardingUser);

    store.commit("setLoading", false);
    store.commit("setShowLoadingOverlay", false);
  });

  // routes control flow
  router.beforeEach(async (to, from, next) => {
    const isFromStartLocation = from === START_LOCATION;

    const tryToAuthenticate = to.meta.tryToAuthenticate;

    const requiresAuth = to.meta.requiresAuth;

    let token = store.getters.token;

    let isAuthenticated = store.getters.isAuthenticated;

    function doNextToRouteName(name) {
      if (from.name === name) {
        store.commit("setLoading", false);
      }
      if (to.name !== name) {
        next({ name });
      } else {
        next();
      }
    }

    function toNextPageOrHome() {
      if (store.getters.nextPage) {
        localStorage.removeItem("nextPage");
        next(store.getters.nextPage);
      } else {
        doNextToRouteName("Home");
      }
    }

    // Redirecionamentos de autenticação

    if (requiresAuth && !isAuthenticated && !token) {
      localStorage.setItem("nextPage", to.fullPath);
      doNextToRouteName("Login");
      return;
    }

    if (requiresAuth && !isAuthenticated && token) {
      store.commit("setIsAuthenticating", true);
      isAuthenticated = await store.dispatch("authentication");
      store.commit("setIsAuthenticating", false);

      if (!isAuthenticated) {
        localStorage.setItem("nextPage", to.fullPath);
        await store.dispatch("logoutWithDialog");
        return;
      }
    }

    if (tryToAuthenticate && !token) {
      next();
      return;
    }

    if (tryToAuthenticate && !isAuthenticated && token) {
      store.commit("setIsAuthenticating", true);
      isAuthenticated = await store.dispatch("authentication");
      store.commit("setIsAuthenticating", false);

      if (!isAuthenticated) {
        await store.dispatch("logoutWithDialog");
        return;
      }
    }

    if (tryToAuthenticate && isAuthenticated && token) {
      toNextPageOrHome();
      return;
    }

    // Redirecionamentos de autorização

    const { isConecta, isReseller, isSimulation, isMsRolesConfirmed } =
      store.getters;

    if (to.meta.isConectaRequired && !isConecta) {
      doNextToRouteName("Home");
      return;
    }

    if (to.meta.isResellerRequired && !isReseller) {
      doNextToRouteName("Home");
      return;
    }

    if (isSimulation) {
      next();
      return;
    }

    let allowed = true;

    if (to.meta.planProductRequired) {
      allowed = await store
        .dispatch("checkHasProductPermissions", to.meta.planProductRequired)
        .then((response) => {
          if (response.hasPermission === false && response.code) {
            store.commit("updateRouteError", {
              code: response.code,
              slug: to.meta.planProductRequired,
            });
            return false;
          }
          return true;
        })
        .finally(() => store.commit("setLoading", false));
    }

    if (to.meta.userPermissionRequired && allowed) {
      allowed = await store
        .dispatch("checkHasPagePermissions", to.meta.userPermissionRequired)
        .then((response) => {
          if (response.hasPermission === false && response.code) {
            store.commit("updateRouteError", {
              code: response.code,
              slug: to.meta.userPermissionRequired,
            });
            return false;
          }
          return true;
        })
        .finally(() => store.commit("setLoading", false));
    }

    if (!allowed) {
      if (isFromStartLocation) {
        doNextToRouteName("Home");
      } else {
        const routeError = store.getters.routeError;
        if (routeError.code) {
          commitErrorReport(routeError.code, routeError.slug);
          store.commit("updateRouteError", {});
        }
        store.commit("setLoading", false);
      }
      return;
    }

    // Tenta pular etapas de verificação

    const trialAllowedBySales = localStorage.getItem("allow_trial");

    let { trialIsAllowed, isNew, appIsInstalled, hasVerifiedPhone } =
      store.getters;

    if (isAuthenticated && trialIsAllowed && isNew && appIsInstalled) {
      await autoInstallApp();
    }

    if (isAuthenticated && !hasVerifiedPhone && trialAllowedBySales) {
      await autoVerifyPhone();
    }

    // Redirecionamentos para guiar o usuário
    // TODO: associar esses redirects a alguma configuração no "meta" da rota para consistência do padrão utilizado

    ({ isNew, appIsInstalled, hasVerifiedPhone } = store.getters);

    const { surveyAnswered, hasEndedTrial, isSuspended, isMicrosoftUser } =
      store.getters;

    if (isAuthenticated && isNew && !surveyAnswered && !trialIsAllowed) {
      doNextToRouteName("OnboardingSurvey");
      return;
    } else if (to.name === "OnboardingSurvey" && !isNew) {
      doNextToRouteName("Home");
      return;
    }

    if (isAuthenticated && isSuspended) {
      doNextToRouteName("Suspended");
      return;
    } else if (to.name === "Suspended" && !isSuspended) {
      doNextToRouteName("Home");
      return;
    }

    if (
      isAuthenticated &&
      trialIsAllowed &&
      (isNew || !appIsInstalled) &&
      !isMicrosoftUser
    ) {
      doNextToRouteName("Install");
      return;
    }

    if (
      isAuthenticated &&
      trialIsAllowed &&
      (isNew || !appIsInstalled) &&
      isMicrosoftUser
    ) {
      store.dispatch("logout");
      localStorage.setItem("auth_error", "AUTHORIZATION_IDENTITYNOTFOUND");
      return;
    }

    if (isAuthenticated && !hasVerifiedPhone) {
      doNextToRouteName("PhoneVerification");
      return;
    } else if (to.name === "PhoneVerification" && hasVerifiedPhone) {
      doNextToRouteName("Home");
      return;
    }

    if (isAuthenticated && hasEndedTrial && !isSimulation) {
      doNextToRouteName("BillingUpgradePlan");
      return;
    }

    if (isAuthenticated && !clarityInitialized) {
      initializeClarity();
      clarityInitialized = true;

      store.dispatch("startHubpostIntegration");
    }

    if (isAuthenticated && isMicrosoftUser && !isMsRolesConfirmed) {
      store.dispatch("ensureMsRoles");
    }

    // Permitir navegação
    next();
  });
}

function commitSuite() {
  store.commit("setPrimaryColor", "primarySuite");
  store.commit("setSecondaryColor", "accentSuite");
  store.commit("removeProductName");
}

function commitTrack() {
  store.commit("setPrimaryColor", "primaryTrack");
  store.commit("setSecondaryColor", "accentTrack");
  store.commit("setProductName", modules.track);
}

function commitToMetaApp(toMetaApp) {
  if (toMetaApp === "conecta_track") {
    commitTrack();
  } else {
    commitSuite();
  }
}

function commitErrorReport(code, slug) {
  const errorReport = {
    show: true,
    status: code,
    app: slug,
  };
  store.commit("setPermissionDialog", errorReport);
}

async function autoInstallApp() {
  store.commit("setShowLoadingOverlay", true);

  await retry(
    async () => {
      store.commit(
        "setLoadingMessages",
        store.getters.getRandomRetryMessages()
      );
      return await store.dispatch("isAppInstalled");
    },
    {
      stop: stopAfterAttempt(maxRetriesCount),
      wait: waitExponential({ min: minWaitTime, max: maxWaitTime }),
      retry: () => {
        const { isNew } = store.getters;
        return isNew;
      },
    }
  );

  store.commit("setLoadingMessages", [" "]);
}

async function autoVerifyPhone() {
  store.commit("setShowLoadingOverlay", true);

  const defaultPhoneSkip = "55 00000";

  await retry(
    async () => {
      const response = await store.dispatch("updateVerifiedPhone", {
        phoneNumber: defaultPhoneSkip,
        status_code: "SKIP_VERIFICATION",
        message: "Skip phone verification because trial is allowed.",
      });
      if (response?.data) {
        store.commit("setCompany", response.data);
      }
      return response;
    },
    {
      stop: stopAfterAttempt(maxRetriesCount),
      wait: waitExponential({ min: minWaitTime, max: maxWaitTime }),
      retry: () => {
        const { hasVerifiedPhone } = store.getters;
        return !hasVerifiedPhone;
      },
    }
  );
}

export default router;
